/**
 * BedSystem — Handles bed interaction, sleeping, and spawn points.
 *
 * Features:
 * - Right-click bed at night = sleep (skip to day)
 * - Right-click bed during day = message "Solo puedes dormir de noche"
 * - Sleeping sets respawn point to bed location
 * - Hostile mobs within 10 blocks prevent sleeping
 * - Sleep animation: screen fades to black, time advances, fades back
 * - If bed is destroyed, spawn point resets to world spawn (0, surface, 0)
 */
export class BedSystem {
  constructor() {
    this.spawnPoint = null;  // { x, y, z } or null (use world spawn)
    this.isSleeping = false;
    this._sleepPhase = 0;    // 0=not sleeping, 1=fading out, 2=sleeping, 3=fading in
    this._sleepTimer = 0;
    this._overlay = null;
    this._sleepText = null;
    this._messageEl = null;
    this._messageTimer = 0;
    this.nightsWithoutSleep = 0;

    this._buildUI();
  }

  _buildUI() {
    // --- Sleep overlay (black screen for fade effect) ---
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'background:#000',
      'z-index:950',
      'opacity:0',
      'pointer-events:none',
      'transition:none',
      'display:flex',
      'align-items:center',
      'justify-content:center',
    ].join(';');
    this._overlay = overlay;

    // "Durmiendo..." text shown while sleeping
    const sleepText = document.createElement('div');
    sleepText.textContent = 'Durmiendo...';
    sleepText.style.cssText = [
      'color:#fff',
      'font-family:Courier New, monospace',
      'font-size:24px',
      'text-shadow:2px 2px 4px #000',
      'opacity:0',
      'letter-spacing:2px',
    ].join(';');
    overlay.appendChild(sleepText);
    this._sleepText = sleepText;

    document.body.appendChild(overlay);

    // --- Message element ("Solo puedes dormir de noche", etc.) ---
    const msg = document.createElement('div');
    msg.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:120px',
      'transform:translateX(-50%)',
      'font-family:Courier New, monospace',
      'font-size:18px',
      'color:#fff',
      'text-shadow:2px 2px 0 #000, -1px -1px 0 #000',
      'z-index:951',
      'opacity:0',
      'pointer-events:none',
      'white-space:nowrap',
      'transition:opacity 0.4s ease',
    ].join(';');
    this._messageEl = msg;
    document.body.appendChild(msg);
  }

  /**
   * Try to sleep in a bed.
   * @param {number} bx - Bed block X position
   * @param {number} by - Bed block Y position
   * @param {number} bz - Bed block Z position
   * @param {boolean} isNight - Is it nighttime?
   * @param {Array} hostileMobs - Nearby hostile mob entities
   * @returns {string|null} Error message if can't sleep, null if sleeping started
   */
  tryToSleep(bx, by, bz, isNight, hostileMobs) {
    if (this.isSleeping) return 'Ya estas durmiendo';

    if (!isNight) return 'Solo puedes dormir de noche';

    // Check for hostile mobs within 10 blocks
    const nearbyHostile = hostileMobs.some(mob => {
      const dx = mob.position.x - bx;
      const dy = mob.position.y - by;
      const dz = mob.position.z - bz;
      return Math.sqrt(dx * dx + dy * dy + dz * dz) < 10;
    });
    if (nearbyHostile) return 'No puedes dormir, hay monstruos cerca';

    // Start sleeping
    this.spawnPoint = { x: bx, y: by + 1, z: bz }; // spawn above bed
    this.isSleeping = true;
    this.nightsWithoutSleep = 0;
    this._sleepPhase = 1; // fade out
    this._sleepTimer = 0;
    return null;
  }

  /**
   * Update sleep animation.
   * @param {number} dt - Delta time in seconds
   * @returns {{ skipToDay: boolean, wakeUp: boolean }}
   */
  update(dt) {
    const result = { skipToDay: false, wakeUp: false };

    // Update message display timer
    if (this._messageTimer > 0) {
      this._messageTimer -= dt;
      if (this._messageTimer <= 0) {
        this._messageEl.style.opacity = '0';
      }
    }

    if (!this.isSleeping) return result;

    this._sleepTimer += dt;

    switch (this._sleepPhase) {
      case 1: { // Fading out (1.5 seconds)
        const alpha = Math.min(1, this._sleepTimer / 1.5);
        this._overlay.style.opacity = String(alpha);
        // Show sleeping text once fully dark
        if (alpha >= 0.95) {
          this._sleepText.style.opacity = '1';
        }
        if (this._sleepTimer >= 1.5) {
          this._overlay.style.opacity = '1';
          this._sleepText.style.opacity = '1';
          this._sleepPhase = 2;
          this._sleepTimer = 0;
          result.skipToDay = true; // Tell main.js to set time to day
        }
        break;
      }
      case 2: { // Black screen (1 second)
        if (this._sleepTimer >= 1.0) {
          this._sleepPhase = 3;
          this._sleepTimer = 0;
        }
        break;
      }
      case 3: { // Fading in (1.5 seconds)
        const alpha = Math.max(0, 1 - this._sleepTimer / 1.5);
        this._overlay.style.opacity = String(alpha);
        // Hide sleeping text as it fades
        if (alpha < 0.5) {
          this._sleepText.style.opacity = '0';
        }
        if (this._sleepTimer >= 1.5) {
          this._sleepPhase = 0;
          this.isSleeping = false;
          this._overlay.style.opacity = '0';
          this._sleepText.style.opacity = '0';
          result.wakeUp = true;
        }
        break;
      }
    }

    return result;
  }

  /**
   * Display a temporary message on screen (above hotbar area).
   * @param {string} text - Message to display
   */
  showMessage(text) {
    this._messageEl.textContent = text;
    this._messageEl.style.opacity = '1';
    this._messageTimer = 3;
  }

  /**
   * Get the current respawn point (bed location) or null for world spawn.
   * @returns {{ x: number, y: number, z: number } | null}
   */
  getSpawnPoint() {
    return this.spawnPoint;
  }

  /**
   * Called when a bed block is destroyed. Resets spawn point if it matches.
   * @param {number} bx - Destroyed bed X
   * @param {number} by - Destroyed bed Y
   * @param {number} bz - Destroyed bed Z
   */
  onBedDestroyed(bx, by, bz) {
    if (
      this.spawnPoint &&
      this.spawnPoint.x === bx &&
      this.spawnPoint.y === by + 1 &&
      this.spawnPoint.z === bz
    ) {
      this.spawnPoint = null;
    }
  }

  /**
   * Serialize bed system state for saving.
   * @returns {{ spawnPoint: { x: number, y: number, z: number } | null }}
   */
  serialize() {
    return { spawnPoint: this.spawnPoint };
  }

  /**
   * Restore bed system state from saved data.
   * @param {object} data - Previously serialized data
   */
  deserialize(data) {
    if (data && data.spawnPoint) {
      this.spawnPoint = data.spawnPoint;
    }
  }
}
